/**
 * Milestone Manager
 * Checks milestone conditions against player state and awards milestones.
 * Listens to game events via EventBus to trigger checks at appropriate times.
 */

import { GameEvent, MilestoneRewardType } from '@/types';
import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { saveManager } from '@/systems/persistence/SaveManager';
import {
  MILESTONE_DEFINITIONS,
  MILESTONE_CONDITIONS,
  MilestoneTransientData,
} from '@/data/milestones/milestoneData';

export class MilestoneManager {
  /** Current catch streak (reset on miss) */
  private currentStreak: number = 0;

  /** Total hauler trips completed this session (accumulated via events) */
  private haulerTripsCompleted: number = 0;

  private unsubscribers: (() => void)[] = [];

  /**
   * Initialize the milestone manager and subscribe to relevant events.
   */
  initialize(): void {
    // Fish caught — update streak, check milestones
    this.unsubscribers.push(
      eventBus.on(GameEvent.FISH_CAUGHT, () => {
        this.currentStreak += 1;
        this.checkMilestones();
      }),
    );

    // Fish lost — reset streak
    this.unsubscribers.push(
      eventBus.on(GameEvent.FISH_LOST, () => {
        this.currentStreak = 0;
      }),
    );

    // Fish sold — check economy milestones
    this.unsubscribers.push(
      eventBus.on(GameEvent.FISH_SOLD, () => {
        this.checkMilestones();
      }),
    );

    // Buddy hired — check buddy milestones
    this.unsubscribers.push(
      eventBus.on(GameEvent.BUDDY_HIRED, () => {
        this.checkMilestones();
      }),
    );

    // Hauler hired — check buddy milestones
    this.unsubscribers.push(
      eventBus.on(GameEvent.HAULER_HIRED, () => {
        this.checkMilestones();
      }),
    );

    // Hauler returned — increment trip count, check milestones
    this.unsubscribers.push(
      eventBus.on(GameEvent.HAULER_RETURNED, () => {
        this.haulerTripsCompleted += 1;
        this.checkMilestones();
      }),
    );

    // Equipment purchased — may affect state-based milestones
    this.unsubscribers.push(
      eventBus.on(GameEvent.EQUIPMENT_PURCHASED, () => {
        this.checkMilestones();
      }),
    );

    // Trap pulled — check trapping milestones
    this.unsubscribers.push(
      eventBus.on(GameEvent.TRAP_PULLED, () => {
        this.checkMilestones();
      }),
    );

    // Game loaded — restore hauler trip count from playerState, run initial check
    this.unsubscribers.push(
      eventBus.on(GameEvent.GAME_LOADED, () => {
        // Restore hauler trips from stats (approximate from hauler buddy data)
        const state = useGameStore.getState();
        let totalTrips = 0;
        for (const hauler of state.playerState.haulerBuddies) {
          totalTrips += hauler.totalTripsCompleted ?? 0;
        }
        this.haulerTripsCompleted = totalTrips;
        this.checkMilestones();
      }),
    );

    console.log(`🏆 MilestoneManager initialized with ${MILESTONE_DEFINITIONS.length} milestones`);
  }

  /**
   * Cleanup event subscriptions
   */
  cleanup(): void {
    for (const unsub of this.unsubscribers) {
      unsub();
    }
    this.unsubscribers = [];
  }

  /**
   * Check all unearned milestones against current state.
   * Awards any newly-met milestones.
   */
  checkMilestones(): void {
    const state = useGameStore.getState();
    const earnedMilestones = state.playerState.earnedMilestones ?? [];

    const transientData: MilestoneTransientData = {
      currentStreak: this.currentStreak,
      haulerTripsCompleted: this.haulerTripsCompleted,
    };

    for (const milestone of MILESTONE_DEFINITIONS) {
      // Skip already earned
      if (earnedMilestones.includes(milestone.id)) {
        continue;
      }

      // Check condition
      const conditionFn = MILESTONE_CONDITIONS[milestone.conditionKey];
      if (!conditionFn) {
        console.warn(`⚠️ No condition function for milestone: ${milestone.conditionKey}`);
        continue;
      }

      if (conditionFn(state.playerState, transientData)) {
        this.awardMilestone(milestone.id);
      }
    }
  }

  /**
   * Award a milestone: mark as earned, apply rewards, emit event, save.
   */
  private awardMilestone(milestoneId: string): void {
    const milestone = MILESTONE_DEFINITIONS.find((m) => m.id === milestoneId);
    if (!milestone) return;

    console.log(`🏆 Milestone achieved: ${milestone.name}`);

    // Mark as earned
    useGameStore.getState().addEarnedMilestone(milestoneId);

    // Apply rewards
    for (const reward of milestone.rewards) {
      switch (reward.type) {
        case MilestoneRewardType.CURRENCY:
          useGameStore.getState().addCurrency(reward.value);
          break;
        case MilestoneRewardType.INVENTORY_EXPANSION:
          useGameStore.setState((draft) => {
            draft.playerState.inventory.capacity += reward.value;
          });
          break;
        case MilestoneRewardType.EXPERIENCE:
          // Future: award XP
          break;
      }
    }

    // Emit event for UI notification
    eventBus.emit(GameEvent.MILESTONE_ACHIEVED, { milestone });

    // Save after milestone
    saveManager.save();
  }

  /**
   * Get the current catch streak (for UI display if needed)
   */
  getCurrentStreak(): number {
    return this.currentStreak;
  }
}

// Singleton instance
export const milestoneManager = new MilestoneManager();
